import React from 'react';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import { t } from 'ttag';
import moment from 'moment';

import { useMutation } from '@apollo/react-hooks';
import Form from '../../../components/Form';
import FormField from '../../../components/FormField';
import Loading from '../../../components/Loading';
import {
  CATEGORY_OPTIONS,
  GENDER_OPTIONS,
  USER_STATUS,
} from '../../../utils/enums';
import FormBox from '../../../components/FormBox';
import ErrorMessage from '../../../components/ErrorMessage';
import ChangePasswordDialog from '../../../components/ChangePassword';
import useApi from '../../../api';
import useEditForm from '../../../hooks/useEditForm';
import useCreateForm from '../../../hooks/useCreateForm';
import useUser from '../../../auth/hooks';
import ImageCropper from '../../../components/ImageCropper';
import { isManager, isSuperManager } from '../../../utils/permissions';
import ReasonDialogForm from '../../../components/ReasonDialogForm';
import { extractDataError } from '../../../utils/errors';
import useNotifier from '../../../components/Messages/hooks';
import LoadingButton from '../../../components/LoadingButton';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { TabPanel, useTabs } from '../../../components/Tab/Tabs';
import { AdditionTab } from '../components/TabAddition';

const TABS = {
  info: 0,
  addition: 1,
};

const BaseForm = ({
  id,
  loading,
  edit,
  initial,
  onSubmit,
  errors,
  trainer,
  showPasswordField,
  onActivate,
  onArchivate,
}) => {
  const api = useApi();
  const [isArchiveReasonDialog, toggleReasonArhiveDialog] =
    React.useState(false);
  const { user } = useUser();
  return (
    <Form onSubmit={onSubmit} initial={initial} errors={errors}>
      {({ data, change, errors, submit }) => (
        <Grid container spacing={4}>
          <Grid item sm={6}>
            <FormBox title={t`Основна інформація`}>
              {user.isAdmin && (
                <FormField
                  required
                  fullWidth
                  query={api.schema.clubs.many()[0]}
                  preInit={edit}
                  dataKey='clubs'
                  variables={{}}
                  type='remoteSelect'
                  margin='normal'
                  label={t`Оберіть клуб`}
                  id='clubId'
                  name='clubId'
                  variant='outlined'
                  value={data.clubId}
                  errors={errors.club_id}
                  labelKey='fullName'
                  onChange={change}
                />
              )}
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`Прізвище`}
                id='lastName'
                name='lastName'
                autoComplete='lastName'
                variant='outlined'
                value={data.lastName}
                errors={errors.last_name}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`Ім'я`}
                id='firstName'
                name='firstName'
                autoComplete='firstName'
                variant='outlined'
                value={data.firstName}
                errors={errors.first_name}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`По-батькові`}
                id='middleName'
                name='middleName'
                autoComplete='middleName'
                variant='outlined'
                value={data.middleName}
                errors={errors.middle_name}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`Прізвище Ім’я (Eng)`}
                id='enFullName'
                name='enFullName'
                autoComplete='middleName'
                variant='outlined'
                value={data.enFullName}
                errors={errors.en_full_name}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                type='date'
                margin='normal'
                label={t`Дата народження`}
                id='birthday'
                name='birthday'
                autoComplete='birthday'
                variant='outlined'
                value={data.birthday}
                errors={errors.birthday}
                onChange={change}
              />
              {showPasswordField && (
                <FormField
                  required
                  fullWidth
                  type='password'
                  margin='normal'
                  label={t`Пароль`}
                  id='password'
                  name='password'
                  autoComplete='password'
                  variant='outlined'
                  value={data.password}
                  errors={errors.password}
                  onChange={change}
                />
              )}
              <FormField
                required
                fullWidth
                type='select'
                margin='normal'
                label={t`Стать`}
                id='gender'
                name='gender'
                autoComplete='gender'
                variant='outlined'
                value={data.gender}
                errors={errors.gender}
                options={GENDER_OPTIONS}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                disabled
                type='select'
                margin='normal'
                label={t`Категорія`}
                id='category'
                name='category'
                autoComplete='category'
                variant='outlined'
                value={data.category}
                errors={errors.category}
                options={CATEGORY_OPTIONS}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                disabled
                type='date'
                margin='normal'
                label={t`Cтрок ліцензії`}
                id='licenseTerm'
                name='licenseTerm'
                autoComplete='licenseTerm'
                variant='outlined'
                value={data.licenseTerm}
                errors={errors.license_term}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                query={api.schema.common.sections()[0]}
                preInit={edit}
                dataKey='sections'
                variables={{}}
                type='multi_select'
                margin='normal'
                label={t`Оберіть вид спорту`}
                id='sections'
                name='sections'
                variant='outlined'
                value={data.sections}
                errors={errors.sections}
                labelKey='name'
                onChange={change}
              />
            </FormBox>
          </Grid>
          <ReasonDialogForm
            open={isArchiveReasonDialog}
            onClose={() => toggleReasonArhiveDialog(false)}
            onAccept={(data) => {
              onArchivate(data.reason);
              toggleReasonArhiveDialog(false);
            }}
          />
          <Grid item sm={6}>
            <FormBox grid title={t`Фото тренера`}>
              <FormControl variant='outlined' fullWidth margin='normal'>
                <ImageCropper
                  name='photo'
                  objectType='user_photo'
                  onChange={change}
                  fileObject={data.photo}
                />
                <ErrorMessage errors={errors.photo} />
              </FormControl>
            </FormBox>
            <FormBox title={t`Контактна інформація`}>
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`Email`}
                id='email'
                name='email'
                autoComplete='email'
                variant='outlined'
                value={data.email}
                errors={errors.email}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`Телефон`}
                id='phone'
                name='phone'
                autoComplete='phone'
                variant='outlined'
                value={data.phone}
                errors={errors.phone}
                onChange={change}
              />
              <FormField
                required
                fullWidth
                type='text'
                margin='normal'
                label={t`Адреса`}
                id='address'
                name='address'
                autoComplete='address'
                variant='outlined'
                value={data.address}
                errors={errors.address}
                onChange={change}
              />
            </FormBox>
            <FormBox title={t`Атестації`}>
              <Typography gutterBottom>{t`Данні відсутні`}</Typography>
            </FormBox>
            {edit && id && (
              <FormBox title={t`Налаштування`}>
                <br />
                <ChangePasswordDialog userId={id} />
              </FormBox>
            )}
          </Grid>
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <LoadingButton
                loading={loading}
                style={{ margin: '20px 0 20px 0' }}
                variant='contained'
                color='primary'
                onClick={submit}
              >
                {t`Зберегти`}
              </LoadingButton>
            </Grid>
            {trainer && (
              <>
                {/* TODO use permission */}
                {(user.isAdmin || isManager(user) || isSuperManager(user)) &&
                  trainer.status !== USER_STATUS.active && (
                    <Grid item sm={6}>
                      <Button
                        style={{ margin: '20px 0 20px 0' }}
                        margin='normal'
                        type='button'
                        fullWidth
                        variant='outlined'
                        color='primary'
                        onClick={onActivate}
                      >
                        {t`Активувати`}
                      </Button>
                    </Grid>
                  )}
                {/* TODO use permission */}
                {(user.isAdmin || isManager(user) || isSuperManager(user)) &&
                  trainer.status !== USER_STATUS.archived && (
                    <Grid item sm={6}>
                      <Button
                        style={{ margin: '20px 0 20px 0' }}
                        margin='normal'
                        type='button'
                        fullWidth
                        variant='outlined'
                        color='default'
                        onClick={() => toggleReasonArhiveDialog(true)}
                      >
                        {t`В архів`}
                      </Button>
                    </Grid>
                  )}
              </>
            )}
          </Grid>
        </Grid>
      )}
    </Form>
  );
};

const getInitialData = (row) => ({
  clubId: row.clubId || null,
  firstName: row.firstName || '',
  lastName: row.lastName || '',
  middleName: row.middleName || '',
  email: row.email || '',
  gender: row.gender || undefined,
  photo: row.photo || '',
  password: '',
  enFullName: row.enFullName || '',
  phone: row.phone || '',
  address: row.address || '',
  birthday: row.birthday || null,
  category: row.category || null,
  licenseTerm: row.licenseTerm || null,
  sections: row.sections || [],
});

const getPostData = (row) => ({
  clubId: Number(row.clubId) || null,
  firstName: row.firstName || '',
  lastName: row.lastName || '',
  middleName: row.middleName || '',
  email: row.email || '',
  gender: Number(row.gender) || null,
  photo: (row.photo && row.photo.id) || '',
  enFullName: row.enFullName || '',
  phone: row.phone || '',
  address: row.address || '',
  password: row.password || '',
  birthday: (row.birthday && moment(row.birthday).format('YYYY-MM-DD')) || null,
  sections: row.sections || [],
  // category: row.category || null,
  // licenseTerm: row.licenseTerm || null,
});

export const EditForm = () => {
  const [tab, onChange] = useTabs({ tabs: TABS, defaultTab: TABS.info });
  const api = useApi();
  const pushMessage = useNotifier();
  const { id, loading, submitLoading, data, onSubmit, errors, refetch } =
    useEditForm({
      schema: api.schema.trainers,
      router: api.router.trainers,
    });

  const [activateTrainer] = useMutation(api.schema.trainers.activate()[0], {
    onCompleted: (d) => {
      const { activateTrainer: res } = d;
      if (res.ok) {
        pushMessage({ text: 'Успішно активовано' });
        refetch();
      } else {
        const dataError = extractDataError(res.errors);
        pushMessage({ text: dataError });
      }
    },
  });

  const [archivateTrainer] = useMutation(api.schema.trainers.archivate()[0], {
    onCompleted: (d) => {
      const { archivateTrainer: res } = d;
      if (res.ok) {
        pushMessage({ text: 'Успішно додано до архіву' });
        refetch();
      } else {
        const dataError = extractDataError(res.errors);
        pushMessage({ text: dataError });
      }
    },
  });

  if (loading) return <Loading />;

  return (
    <>
      <h2>{t`Редагувати тренера: ${data.fullName}`}</h2>
      <Paper>
        <Box m={4}>
          <Tabs
            value={tab}
            indicatorColor='primary'
            textColor='primary'
            onChange={(e, v) => onChange(v)}
          >
            <Tab label={t`Основна Інформація`} disabled={false} />
            <Tab label={t`Додатково`} disabled={false} />
          </Tabs>
          <TabPanel value={tab} index={TABS.info}>
            <BaseForm
              id={id}
              loading={submitLoading}
              trainer={data}
              initial={getInitialData(data)}
              onSubmit={(formData) => onSubmit(getPostData(formData))}
              errors={errors}
              edit
              showPasswordField={false}
              onActivate={() => activateTrainer({ variables: { id } })}
              onArchivate={(reason) =>
                archivateTrainer({ variables: { id, reason } })
              }
            />
          </TabPanel>
          <TabPanel value={tab} index={TABS.addition}>
            <AdditionTab trainerId={data.id} />
          </TabPanel>
        </Box>
      </Paper>
    </>
  );
};

export const CreateForm = () => {
  const api = useApi();
  const { loading, onSubmit, errors } = useCreateForm({
    schema: api.schema.trainers,
    router: api.router.trainers,
  });

  if (loading) return <Loading />;

  return (
    <>
      <h2>{t`Додати тренера`}</h2>
      <Paper>
        <Box m={4}>
          <BaseForm
            initial={getInitialData({})}
            onSubmit={(formData) => onSubmit(getPostData(formData))}
            errors={errors}
            edit={false}
            showPasswordField
          />
        </Box>
      </Paper>
    </>
  );
};
